import {
  getTableListByIndex,
  setTableList,
  tableObjectStoreClear,
  tableObjectStoreDelete,
  tableObjectStoreGet,
  tableObjectStoreGetAll,
  tableObjectStoreMerge,
  tableObjectStorePut,
} from 'db/offline-db';
import moment from 'moment';
import sortBy from 'lodash.sortby';

export const OFFLINE_FEED_ORDER_STATUSES = {
  pending: 'offline_pending',
  failed: 'offline_failed',
};

// Keeps the server order (farms by name, groups by last check, bins naturally) after IndexedDB sorts by id
const withPositions = (resources) => resources.map((resource, position) => ({ ...resource, position }));
const byPosition = (resources) => sortBy(resources, 'position');

// Same shape as a paginated API response, so the pickers work unchanged
const paginateOffline = (resources, { page = 1, per_page = 25, search } = {}) => {
  const query = (search || '').toLowerCase();
  const matched = query
    ? resources.filter(({ name }) => (name || '').toLowerCase().includes(query))
    : resources;
  const offset = (page - 1) * per_page;
  return { resources: matched.slice(offset, offset + per_page), meta: { total: matched.length } };
};

// preloaded data
export const setFeedOrderFarmsOffline = (resources) => setTableList('feed_order_farms', withPositions(resources));
export const setFeedOrderPigGroupsOffline = (resources) => (
  setTableList('feed_order_pig_groups', withPositions(resources))
);
export const setFeedOrderBinsOffline = (resources) => setTableList('feed_order_bins', withPositions(resources));

// The farm header shows the open groups count the online endpoint adds
export const getFeedOrderFarmOffline = async (farmId) => {
  const farm = await tableObjectStoreGet('feed_order_farms', 'id', Number(farmId));
  if (!farm) return farm;
  const groups = await getTableListByIndex('feed_order_pig_groups', 'farm_id', Number(farmId));
  return { ...farm, open_pig_groups_count: groups.filter(({ status }) => status === 'opened').length };
};
export const getFeedOrderPigGroupOffline = (groupId) => (
  tableObjectStoreGet('feed_order_pig_groups', 'id', Number(groupId))
);
export const getFeedOrderFarmBinsOffline = (farmId) => (
  getTableListByIndex('feed_order_bins', 'farm_id', Number(farmId)).then(byPosition)
);
export const getFeedOrderFarmsOffline = (params) => (
  tableObjectStoreGetAll('feed_order_farms').then((farms) => paginateOffline(byPosition(farms), params))
);
export const getFeedOrderFarmGroupsOffline = (farmId, params) => (
  getTableListByIndex('feed_order_pig_groups', 'farm_id', Number(farmId))
    .then((groups) => paginateOffline(byPosition(groups), params))
);

// last loaded lists
// History stays online only, but the page the user last saw is kept so going offline does not empty the list.
// Cleared with the daily preload and on logout.
export const saveFeedOrdersListOffline = (key, { resources, meta }) => (
  tableObjectStorePut('feed_order_lists', { key, resources, meta, cached_at: moment().toISOString() })
);
export const getFeedOrdersListOffline = (key) => (
  tableObjectStoreGetAll('feed_order_lists').then((lists) => {
    const cached = lists.find((list) => list.key === key);
    return cached ? { resources: cached.resources, meta: cached.meta } : null;
  })
);
export const clearFeedOrdersListsOffline = () => tableObjectStoreClear('feed_order_lists');

// Kept next to the daily preload date of the company
export const updateFeedOrdersPreloadDate = (companyId) => (
  tableObjectStoreMerge('last_preload_data', {
    current_company_id: companyId,
    feed_orders_loaded_at: moment().format(),
  }, 'current_company_id')
);

// orders on the device
// The feed_order_requests store keeps two kinds of rows under the client_uuid key:
// - orders waiting for sync (status offline_pending / offline_failed)
// - the last order the server received, with its server id, status and created_at (submitted_at is set),
//   so the user can verify their most recent order while offline

export const isPendingFeedOrder = (order) => (
  Object.values(OFFLINE_FEED_ORDER_STATUSES).includes(order?.status)
);

const isSubmittedFeedOrder = (order) => !!order?.submitted_at;

// Only the author's orders in the current company: the API places an order under the current company
const belongsTo = ({ userId, companyId, farmId }) => (order) => (
  order.user_id === userId
  && order.company_id === companyId
  && (!farmId || order.farm.id === Number(farmId))
);

export const savePendingFeedOrder = (order) => tableObjectStoreMerge('feed_order_requests', order, 'client_uuid');
export const deletePendingFeedOrder = (clientUuid) => tableObjectStoreDelete('feed_order_requests', clientUuid);
export const getPendingFeedOrder = (clientUuid) => (
  tableObjectStoreGet('feed_order_requests', 'client_uuid', clientUuid)
);

export const getPendingFeedOrders = (scope) => (
  tableObjectStoreGetAll('feed_order_requests')
    .then((orders) => orders.filter(isPendingFeedOrder).filter(belongsTo(scope)))
    .then((orders) => sortBy(orders, 'queued_at').reverse())
);

export const getLastSubmittedFeedOrder = (scope) => (
  tableObjectStoreGetAll('feed_order_requests')
    .then((orders) => orders.filter(isSubmittedFeedOrder).filter(belongsTo(scope)))
    .then((orders) => sortBy(orders, 'submitted_at').pop() || null)
);

// Turns a queued order into the stored one the server returned and drops the previously remembered order
// of the same user and company, so only the most recent one stays on the device
export const markFeedOrderSubmitted = async (order, resource) => {
  const { user_id, company_id } = order;
  const previous = await tableObjectStoreGetAll('feed_order_requests')
    .then((orders) => orders.filter(isSubmittedFeedOrder).filter(belongsTo({ userId: user_id, companyId: company_id })))
    .catch(() => []);
  for (const previousOrder of previous) {
    if (previousOrder.client_uuid !== order.client_uuid) await deletePendingFeedOrder(previousOrder.client_uuid);
  }
  return savePendingFeedOrder({
    ...order,
    id: resource.id,
    status: resource.status,
    created_at: resource.created_at,
    delivery_date: resource.delivery_date || order.delivery_date,
    notes: resource.notes ?? order.notes,
    submitted_at: moment().toISOString(),
    error: null,
    error_key: null,
  });
};

// Row for the feed order lists; created_at stays empty until the server receives the order
export const buildPendingFeedOrder = ({ resource, pigGroup, bins, userId, companyId }) => {
  const orderedBins = resource.feed_order_bins_attributes.filter((bin) => !bin._destroy);
  const orderedBinIds = orderedBins.map((bin) => bin.bin_id);

  return {
    id: resource.client_uuid,
    client_uuid: resource.client_uuid,
    user_id: userId,
    company_id: companyId,
    farm: { id: pigGroup.farm.id, name: pigGroup.farm.name },
    pig_group: { id: pigGroup.id, name: pigGroup.name },
    delivery_date: resource.delivery_date,
    notes: resource.notes,
    bin_names: bins.filter((bin) => orderedBinIds.includes(bin.id)).map((bin) => bin.name),
    quantity: orderedBins.reduce((sum, bin) => sum + Number(bin.quantity || 0), 0),
    resource,
    queued_at: moment().toISOString(),
    status: OFFLINE_FEED_ORDER_STATUSES.pending,
    error: null,
    error_key: null,
  };
};

export const isPastDeliveryDate = (deliveryDate) => moment(deliveryDate).isBefore(moment(), 'day');

// Form values of a queued order, to fix and resend it. A passed delivery date is dropped so the form
// offers its default date instead
export const getFeedOrderFormValues = (order) => {
  const { delivery_date, notes, feed_order_bins_attributes } = order.resource;

  return {
    delivery_date: delivery_date && !isPastDeliveryDate(delivery_date) ? moment(delivery_date) : null,
    notes: notes || '',
    feed_order_bins_attributes: feed_order_bins_attributes
      .filter((bin) => !bin._destroy)
      .map(({ bin_id, quantity }) => ({ bin_id, quantity })),
  };
};

export const getRetryFeedOrderPath = ({ farm, pig_group, client_uuid }) => (
  `/feed-orders-farms/${farm.id}/groups/${pig_group.id}/feed-orders/create?retry=${client_uuid}`
);
